import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import StatusBadge from "@/components/StatusBadge";
import BarcodeScanner from "@/components/BarcodeScanner";
import ProductImageThumb from "@/components/ProductImageThumb";
import { FilterTabs } from "@/components/NotificationBanner";
import Toast from "@/components/Toast";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import RefreshButton from "@/components/RefreshButton";
import InboundPanel, { InboundTabs } from "@/components/inbound/InboundPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Package, ArrowRight, ChevronLeft, Trash2, Camera, Search, CheckCircle, BoxIcon, Lock, MessageSquareWarning } from "lucide-react";
import { PALLET, ITEM } from "@/constants/testIds";

// Same states the backend accepts for a revision request (work_revisions.REQUESTABLE_STATES).
const CORRECTABLE_WORK_STATES = ["PLANNING", "SUBMITTED", "BOXING"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };

export default function USWarehouse() {
  const { t } = useLang();
  const [pallets, setPallets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [filterMode, setFilterMode] = useState("active"); // "active" | "history"
  const [showCreate, setShowCreate] = useState(false);
  const [palletName, setPalletName] = useState("");
  const [palletNotes, setPalletNotes] = useState("");
  const [showAddItem, setShowAddItem] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchRes, setSearchRes] = useState([]);
  const [searched, setSearched] = useState("");  // the text of the last finished search
  const [picked, setPicked] = useState(null);
  const [qty, setQty] = useState(1);
  const [showScanner, setShowScanner] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmDeletePallet, setConfirmDeletePallet] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [correctionWork, setCorrectionWork] = useState(null);
  const [correctionNote, setCorrectionNote] = useState("");
  const [stepperDraft, setStepperDraft] = useState({});
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  // Routes this staff member may open pallets for; empty until a warehouse assignment and a route profile exist.
  const [routeOptions, setRouteOptions] = useState([]);
  const [routeIndex, setRouteIndex] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [work, setWork] = useState([]);
  const [confirmPhysical, setConfirmPhysical] = useState(null); // "ready" | "ship"
  const [tab, setTab] = useState("pallets"); // "pallets" | "inbound" | "stock"
  const [hasInbound, setHasInbound] = useState(false);
  const searchTimerRef = useRef(null);

  const showMsg = (type, text) => setMsg({ type, text });

  // `silent` is for the 10 s auto refresh: a brief network drop must not pop an error every time.
  const fetchPallets = async (silent = false) => {
    try {
      const param = filterMode === "history" ? "show_history=true" : "show_all=false";
      const { data } = await axios.get(`${API}/pallets?view=SENDER&${param}`, ax);
      setPallets(data);
      return data;
    } catch (e) {
      if (!silent) showMsg("error", e.response?.data?.detail || t("adm.error"));
    }
  };

  const fetchItems = async (id) => {
    const { data } = await axios.get(`${API}/pallets/${id}/items`, ax);
    setItems(data);
  };

  const fetchWork = async (pid) => {
    try {
      const { data } = await axios.get(`${API}/pallets/${pid}/work`, ax);
      setWork(data);
      const history = await Promise.all(data.map(row => axios.get(`${API}/pallet-work/${row.id}/revisions`, ax)
        .then(({ data: rows }) => rows.map(revision => ({ ...revision, tenant_name: row.tenant_name })))
        .catch(() => [])));
      setRevisions(history.flat().sort((a, b) => b.requested_at.localeCompare(a.requested_at)));
    } catch { setWork([]); setRevisions([]); }
  };

  useEffect(() => { fetchPallets(); }, [filterMode]);

  // Reloads the list and, when a pallet is open, its status, items and tenant work.
  const reloadScreen = async () => {
    const list = await fetchPallets(true);
    if (!selected) return;
    const fresh = list?.find(p => p.id === selected.id);
    if (fresh) setSelected(prev => (prev?.id === fresh.id ? { ...prev, ...fresh } : prev));
    await Promise.all([fetchItems(selected.id), fetchWork(selected.id)]);
  };
  const { refresh, refreshing } = useAutoRefresh(reloadScreen, {
    paused: showCreate || showAddItem || showScanner || !!confirmPhysical || !!confirmDelete || !!confirmDeletePallet || !!correctionWork
      || (!selected && tab !== "pallets"),
  });
  useEffect(() => {
    axios.get(`${API}/pallets/route-options`, ax)
      .then(r => { setRouteOptions(r.data); setRouteIndex(r.data.length === 1 ? "0" : ""); })
      .catch(() => setRouteOptions([]));
    // Inbound/Stock tabs only when a tenant at one of the staff's sender warehouses has the switch on.
    axios.get(`${API}/inbound/options`, ax)
      .then(r => setHasInbound(r.data.warehouses.some(w => w.tenants.length > 0)))
      .catch(() => setHasInbound(false));
  }, []);

  const routeFor = (pallet) => routeOptions.find(r =>
    r.source_warehouse.id === pallet?.source_warehouse_id && r.destination_warehouse.id === pallet?.destination_warehouse_id);
  const tenantName = (id) => routeOptions.flatMap(r => r.tenants).find(x => x.id === id)?.name;
  const routeLabel = (r) => `${r.source_warehouse.name} → ${r.destination_warehouse.name}`;
  const selectedTenants = routeFor(selected)?.tenants || [];
  const physical = selected?.physical_status;
  // Contents are editable while the pallet's physical state is DRAFT.
  const editable = physical === "DRAFT";
  const workFor = (tid) => work.find(w => w.tenant_id === tid);
  const groupName = (tid) => workFor(tid)?.tenant_name || tenantName(tid) || "-";
  const groups = [...new Set(items.map(i => i.tenant_id))].map(tid => ({ key: tid, tenantId: tid, items: items.filter(i => i.tenant_id === tid) }));

  const openPallet = async (p) => {
    setSelected(p); setShowAddItem(false);
    setSearchQ(""); setSearchRes([]); setPicked(null); setQty(1);
    setTenantId(""); setWork([]); setRevisions([]);
    fetchWork(p.id);
    await fetchItems(p.id);
  };

  // The picker lists only the products the chosen tenant approved.
  const chosenTenant = tenantId || (selectedTenants.length === 1 ? selectedTenants[0].id : "");
  const searchProducts = (q) => axios.get(`${API}/products`, { ...ax, params: { q, tenant_id: chosenTenant } });

  const onTenantChange = (id) => { setTenantId(id); setPicked(null); setSearchQ(""); setSearchRes([]); };

  const doSearch = async (q) => {
    if (q.length < 1 || !chosenTenant) { setSearchRes([]); return; }
    const { data } = await searchProducts(q);
    setSearchRes(data.slice(0, 10));
    setSearched(q);
  };

  const onSearchChange = (v) => {
    setSearchQ(v); setPicked(null);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => doSearch(v), 300);
  };

  const onScan = async (code) => {
    setShowScanner(false);
    if (!chosenTenant) { showMsg("error", t("us.select_tenant")); return; }
    setSearchQ(code);
    const { data } = await searchProducts(code);
    if (data.length === 1) { setPicked(data[0]); setSearchRes([]); }
    else { setSearchRes(data.slice(0, 10)); setSearched(code); }
  };

  const pickProduct = (p) => { setPicked(p); setSearchQ(p.product_name); setSearchRes([]); };

  const addItem = async () => {
    if (!picked || qty < 1) return;
    if (!chosenTenant) { showMsg("error", t("us.select_tenant")); return; }
    try {
      const { data } = await axios.post(`${API}/pallets/${selected.id}/items`,
        { asin: picked.asin, quantity: qty, tenant_id: chosenTenant }, ax);
      if (data.stock_warning) showMsg("warning", t("us.msg.over_stock", { n: data.stock_warning.on_shelf }));
      else showMsg("success", t("us.msg.added", { name: picked.product_name, qty }));
      await fetchItems(selected.id);
      fetchWork(selected.id);
      setPicked(null); setSearchQ(""); setQty(1);
    } catch (e) {
      showMsg("error", e.response?.data?.detail || t("adm.error"));
    }
  };

  const updateItemQty = async (item, newQty) => {
    if (newQty < 1) return;
    try {
      const { data } = await axios.put(`${API}/pallets/${selected.id}/items/${item.id}`, { quantity: newQty }, ax);
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, quantity: newQty } : i));
      if (data.stock_warning) showMsg("warning", t("us.msg.over_stock", { n: data.stock_warning.on_shelf }));
    } catch (e) {
      showMsg("error", e.response?.data?.detail || t("adm.error"));
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/pallets/${selected.id}/items/${itemId}`, ax);
      await fetchItems(selected.id);
      fetchWork(selected.id);
      showMsg("success", t("us.msg.removed"));
    } catch (e) {
      showMsg("error", e.response?.data?.detail || t("adm.error"));
    }
    setConfirmDelete(null);
  };

  const createPallet = async () => {
    if (!palletName.trim()) return;
    const route = routeOptions[Number(routeIndex)];
    if (!route) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/pallets`, {
        pallet_name: palletName, notes: palletNotes,
        source_warehouse_id: route.source_warehouse.id, destination_warehouse_id: route.destination_warehouse.id,
      }, ax);
      setPallets(prev => [data, ...prev]);
      setShowCreate(false); setPalletName(""); setPalletNotes("");
      openPallet(data);
      showMsg("success", t("us.msg.created"));
    } catch (e) {
      showMsg("error", e.response?.data?.detail || t("adm.error"));
    } finally { setLoading(false); }
  };

  const runPhysical = async () => {
    const action = confirmPhysical;
    setConfirmPhysical(null);
    try {
      const { data } = await axios.post(`${API}/pallets/${selected.id}/${action}`, {}, ax);
      setSelected(prev => ({ ...prev, physical_status: data.physical_status }));
      showMsg("success", t(action === "ready" ? "us.msg.ready" : "us.msg.shipped"));
      fetchWork(selected.id);
    } catch (e) {
      showMsg("error", e.response?.data?.detail || t("adm.error"));
    }
  };

  const submitCorrection = async () => {
    const note = correctionNote.trim();
    if (!note) return;
    try {
      await axios.post(`${API}/pallet-work/${correctionWork.id}/revisions`, { note }, ax);
      setCorrectionWork(null); setCorrectionNote("");
      showMsg("success", t("recv.correction_sent"));
      await fetchWork(selected.id);
    } catch (e) {
      showMsg("error", e.response?.data?.detail || t("adm.error"));
    }
  };

  const deletePallet = async (id) => {
    try {
      await axios.delete(`${API}/pallets/${id}`, ax);
      setPallets(prev => prev.filter(p => p.id !== id));
      if (selected?.id === id) setSelected(null);
      showMsg("success", t("us.msg.deleted"));
    } catch (e) {
      showMsg("error", e.response?.data?.detail || t("adm.error"));
    }
  };

  if (selected) {
    return (
      <div className="space-y-4 pb-20">
        <Toast msg={msg} onClose={() => setMsg(null)} />

        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => { setSelected(null); fetchPallets(); }} className="gap-1.5 font-semibold border-slate-300 text-slate-700 hover:bg-slate-100">
            <ChevronLeft className="w-4 h-4" /> {t("common.back")}
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">{selected.pallet_name}</h1>
            {routeFor(selected) && <p className="text-xs text-slate-500 truncate">{routeLabel(routeFor(selected))}</p>}
            <StatusBadge status={physical} />
          </div>
          <RefreshButton onClick={refresh} refreshing={refreshing} className="shrink-0" />
          {physical === "DRAFT" && (
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 gap-1.5 shrink-0"
              disabled={items.length === 0} onClick={() => setConfirmPhysical("ready")}>
              <CheckCircle className="w-4 h-4" /> {t("us.mark_ready")}
            </Button>
          )}
          {physical === "READY" && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5 shrink-0"
              onClick={() => setConfirmPhysical("ship")}>
              <ArrowRight className="w-4 h-4" /> {t("us.start_shipment")}
            </Button>
          )}
        </div>

        {physical !== "DRAFT" && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
            <Lock className="w-4 h-4 shrink-0" />
            <span>{t(physical === "READY" ? "us.final_notice" : physical === "IN_TRANSIT" ? "us.shipped_notice" : "us.received_notice")}</span>
          </div>
        )}

        {/* Item list */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="font-semibold text-slate-900">{t("common.items")} ({items.length})</span>
            {editable && (
              <Button size="sm" onClick={() => setShowAddItem(!showAddItem)} data-testid={ITEM.addBtn}
                className="gap-1.5 h-8 text-xs bg-blue-600 hover:bg-blue-700">
                <Plus className="w-3.5 h-3.5" /> {t("us.add_item")}
              </Button>
            )}
          </div>

          {showAddItem && editable && (
            <div className="p-4 bg-blue-50 border-b border-blue-100 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t("us.tenant")} *</Label>
                <Select value={chosenTenant} onValueChange={onTenantChange}>
                  <SelectTrigger className="h-11 bg-white"><SelectValue placeholder={t("us.select_tenant")} /></SelectTrigger>
                  <SelectContent>
                    {selectedTenants.map(x => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchQ}
                    onChange={e => onSearchChange(e.target.value)}
                    placeholder={t("us.search_placeholder")}
                    className="pl-9 h-11"
                    data-testid={ITEM.searchInput}
                  />
                  {searchRes.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-96 overflow-y-auto">
                      {searchRes.map(p => (
                        <button key={p.id} onClick={() => pickProduct(p)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b last:border-0 border-slate-50">
                          <p className="text-sm font-medium text-slate-900">{p.product_name}</p>
                          <p className="text-[10px] text-slate-500 font-mono">ASIN: {p.asin}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowScanner(true)}
                  className="h-11 w-11 p-0 shrink-0" data-testid={ITEM.scanBtn}>
                  <Camera className="w-5 h-5" />
                </Button>
              </div>
              {searchQ && searched === searchQ && searchRes.length === 0 && !picked && (
                <p className="text-xs text-amber-700">{t("us.no_approved_match")}</p>
              )}

              {picked && (
                <div className="p-3 bg-white rounded-xl border border-blue-200 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{picked.product_name}</p>
                      <p className="text-xs text-slate-500 font-mono">{picked.asin}</p>
                    </div>
                    <ProductImageThumb asin={picked.asin} alt={picked.product_name} />
                  </div>
                  <div className="flex items-center gap-3">
                    <Label className="text-sm font-medium whitespace-nowrap">{t("common.quantity")}:</Label>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="h-10 w-10 p-0 text-lg"
                        onClick={() => setQty(Math.max(1, qty - 1))}>−</Button>
                      <Input type="number" inputMode="numeric" value={qty} min={1}
                        onChange={e => setQty(parseInt(e.target.value) || 1)}
                        onFocus={e => e.target.select()}
                        className="w-20 text-center h-10 text-base" data-testid={ITEM.quantityInput} />
                      <Button variant="outline" size="sm" className="h-10 w-10 p-0 text-lg"
                        onClick={() => setQty(qty + 1)}>+</Button>
                    </div>
                    <Button className="h-10 bg-emerald-600 hover:bg-emerald-700 flex-1"
                      onClick={addItem} data-testid={ITEM.confirmAdd}>
                      <Plus className="w-4 h-4 mr-1" /> {t("common.add")}
                    </Button>
                  </div>
                </div>
              )}

            </div>
          )}

          {items.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <BoxIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">{t("us.no_items")}</p>
            </div>
          ) : (
            <div>
              {groups.map(group => (
                <div key={group.key}>
                  {group.tenantId && (
                    <div className="flex items-center justify-between gap-2 px-4 py-2 bg-slate-50 border-y border-slate-100">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-800 truncate">{groupName(group.tenantId)}</p>
                        <p className="text-xs text-slate-500">
                          {t("us.tenant_summary", { count: group.items.length, units: group.items.reduce((n, i) => n + i.quantity, 0) })}
                        </p>
                      </div>
                      {workFor(group.tenantId) && (
                        <div className="flex items-center gap-2 shrink-0">
                          {CORRECTABLE_WORK_STATES.includes(workFor(group.tenantId).status) && (
                            <Button size="sm" variant="outline" className="h-7 gap-1.5 px-2 text-xs" data-testid="us-request-correction-btn"
                              onClick={() => { setCorrectionNote(""); setCorrectionWork(workFor(group.tenantId)); }}>
                              <MessageSquareWarning className="w-3.5 h-3.5" /> {t("recv.request_correction")}
                            </Button>
                          )}
                          {workFor(group.tenantId).has_open_revision && (
                            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800" data-testid="us-correction-open">
                              {t("recv.correction_open")}
                            </span>
                          )}
                          <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                            {t(`us.work.${workFor(group.tenantId).status}`)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
            <div className="divide-y divide-slate-50">
              {group.items.map(item => (
                <div key={item.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{item.product_name}</p>
                    <p className="text-xs text-slate-500 font-mono">{item.asin}</p>
                  </div>
                  {editable ? (
                    /* ── Inline stepper: 44px touch target, 36px visual ── */
                    <div className="flex items-center shrink-0">
                      <button
                        className="flex items-center justify-center w-[44px] h-[44px] disabled:opacity-35"
                        onClick={() => updateItemQty(item, item.quantity - 1)}
                        disabled={item.quantity <= 1}
                      >
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 font-bold text-lg leading-none select-none transition-colors">
                          −
                        </span>
                      </button>
                      <input
                        inputMode="numeric"
                        className="w-11 h-9 text-center font-bold text-sm border border-slate-200 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-text"
                        value={stepperDraft[item.id] ?? String(item.quantity)}
                        onFocus={e => { setStepperDraft(p => ({ ...p, [item.id]: String(item.quantity) })); e.target.select(); }}
                        onChange={e => setStepperDraft(p => ({ ...p, [item.id]: e.target.value }))}
                        onBlur={() => {
                          const v = parseInt(stepperDraft[item.id], 10);
                          if (!isNaN(v) && v >= 1 && v !== item.quantity) updateItemQty(item, v);
                          setStepperDraft(p => { const n = { ...p }; delete n[item.id]; return n; });
                        }}
                        onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
                        data-testid={ITEM.quantityInput + "-" + item.id}
                      />
                      <button
                        className="flex items-center justify-center w-[44px] h-[44px]"
                        onClick={() => updateItemQty(item, item.quantity + 1)}
                      >
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 font-bold text-lg leading-none select-none transition-colors">
                          +
                        </span>
                      </button>
                      <button
                        className="flex items-center justify-center w-[44px] h-[44px] text-red-400 hover:text-red-600"
                        data-testid={ITEM.deleteBtn(item.id)}
                        onClick={() => setConfirmDelete(item)}
                      >
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-red-50 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </span>
                      </button>
                    </div>
                  ) : (
                    <div className="text-right shrink-0">
                      <span className="inline-block bg-slate-100 text-slate-800 font-bold text-sm px-2.5 py-1 rounded-lg">
                        ×{item.quantity}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {revisions.length > 0 && (
          <details className="bg-white rounded-lg border border-slate-200" data-testid="us-revision-history">
            <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-800">
              {t("planning.revision_history", { count: revisions.length })}
            </summary>
            <div className="divide-y divide-slate-100 border-t border-slate-100">
              {revisions.map(revision => (
                <div key={revision.id} className="px-4 py-3">
                  <p className="text-xs text-slate-500">
                    {t(`planning.revision_status.${revision.status.toLowerCase()}`)} · {revision.tenant_name} · {new Date(revision.requested_at).toLocaleString()} · {revision.requested_by_name}
                  </p>
                  <p className="mt-1 text-sm text-slate-800">{revision.note}</p>
                </div>
              ))}
            </div>
          </details>
        )}

        {showScanner && <BarcodeScanner onScan={onScan} onClose={() => setShowScanner(false)} />}

        <AlertDialog open={!!confirmPhysical} onOpenChange={(open) => !open && setConfirmPhysical(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t(confirmPhysical === "ship" ? "us.ship_title" : "us.ready_title")}</AlertDialogTitle>
              <p className="text-sm text-muted-foreground">{t(confirmPhysical === "ship" ? "us.ship_body" : "us.ready_body")}</p>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={runPhysical} className="bg-blue-600 hover:bg-blue-700">{t("common.confirm")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("us.remove_item")}</AlertDialogTitle>
              <p className="text-sm text-muted-foreground">{confirmDelete?.product_name}</p>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={() => deleteItem(confirmDelete.id)} className="bg-red-600 hover:bg-red-700">{t("us.remove")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={!!correctionWork} onOpenChange={open => { if (!open) setCorrectionWork(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t("recv.correction_title")}</DialogTitle>
            </DialogHeader>
            <p className="text-sm font-semibold text-slate-800">{correctionWork?.tenant_name}</p>
            <p className="text-sm text-muted-foreground">{t("recv.correction_body")}</p>
            <div className="space-y-1.5">
              <Label>{t("common.notes")} *</Label>
              <Textarea value={correctionNote} onChange={e => setCorrectionNote(e.target.value)} maxLength={1000} rows={4}
                placeholder={t("recv.note_placeholder")} data-testid="us-correction-input" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCorrectionWork(null)}>{t("common.cancel")}</Button>
              <Button onClick={submitCorrection} disabled={!correctionNote.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="us-correction-submit">
                {t("recv.send")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-4">
      <Toast msg={msg} onClose={() => setMsg(null)} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
          {t("nav.sender_warehouse")}
        </h1>
        {tab === "pallets" && <div className="flex items-center gap-2">
          <FilterTabs mode={filterMode} onChange={setFilterMode} labels={{ active: t("us.filter_active"), history: t("us.filter_history") }} />
          <RefreshButton onClick={refresh} refreshing={refreshing} />
          <Button size="sm"
            data-testid={PALLET.createBtn} className="gap-1.5 h-9 bg-blue-600 hover:bg-blue-700"
            onClick={async () => {
              try {
                const { data } = await axios.get(`${API}/pallets/name-suggest`, ax);
                setPalletName(data.suggested_name);
              } catch {
                setPalletName("");  // suggestion unavailable; the sender types the name
              }
              setPalletNotes("");
              setShowCreate(true);
            }}>
            <Plus className="w-4 h-4" /> {t("us.create_pallet")}
          </Button>
        </div>}
      </div>
      {hasInbound && <InboundTabs value={tab} onChange={setTab} />}

      {tab !== "pallets" ? (
        <InboundPanel side="sender" view={tab} />
      ) : pallets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{t("us.no_pallets")}</p>
          <p className="text-sm mt-1">{t("us.no_pallets_hint")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pallets.map(p => (
            <div key={p.id} data-testid={PALLET.card(p.id)}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group"
              onClick={() => openPallet(p)}>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-900 truncate">{p.pallet_name}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                  <StatusBadge status={p.physical_status} />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-sm text-slate-600">
                    <span className="flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5" />
                      {t("us.types", { count: p.item_count })}
                      {p.total_quantity > 0 && <><span className="text-slate-300">·</span><span>{t("us.units", { count: p.total_quantity })}</span></>}
                    </span>
                  </div>
                  {p.physical_status === "DRAFT" && (
                    <Button variant="ghost" size="sm"
                      className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                      aria-label={t("us.delete_pallet")}
                      data-testid={PALLET.deleteBtn(p.id)}
                      onClick={e => { e.stopPropagation(); setConfirmDeletePallet(p); }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) { setPalletName(""); setPalletNotes(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("us.create_pallet")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("us.pallet_name")} *</Label>
              <Input value={palletName} onChange={e => setPalletName(e.target.value)}
                placeholder={t("us.pallet_name_placeholder")} data-testid={PALLET.nameInput} className="h-11"
                onKeyDown={e => e.key === "Enter" && createPallet()} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.notes")}</Label>
              <Input value={palletNotes} onChange={e => setPalletNotes(e.target.value)} placeholder={t("us.notes_placeholder")} className="h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("us.route")} *</Label>
              {routeOptions.length > 0 ? (
                <Select value={routeIndex} onValueChange={setRouteIndex}>
                  <SelectTrigger className="h-11"><SelectValue placeholder={t("us.select_route")} /></SelectTrigger>
                  <SelectContent>
                    {routeOptions.map((r, i) => <SelectItem key={i} value={String(i)}>{routeLabel(r)}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-amber-700">{t("us.no_routes")}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("common.cancel")}</Button>
            <Button onClick={createPallet} disabled={loading || !palletName.trim() || routeIndex === ""}
              data-testid={PALLET.confirmCreate} className="bg-blue-600 hover:bg-blue-700">
              {loading ? t("us.creating") : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDeletePallet} onOpenChange={() => setConfirmDeletePallet(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("us.delete_pallet")}</AlertDialogTitle>
            <p className="text-sm text-muted-foreground">{confirmDeletePallet?.pallet_name}</p>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => deletePallet(confirmDeletePallet.id)} className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
